// The deal contracts. Two shapes live here and they use different casing on
// purpose:
//
//   IngestItem / IngestPayload  — snake_case, the wire the Node pipeline POSTs.
//   Deal                        — camelCase, what the API returns to the client.
//
// Both are frozen at the Phase 2 gate. The pipeline and the app share this file
// precisely so a field can't drift on one side without breaking the other.

import { z } from 'zod';

export const DEAL_SOURCES = ['fmf', 'scout'] as const;
export const DEAL_STATUSES = ['new', 'saved', 'dismissed'] as const;
export const DEAL_CATEGORIES = [
  'shirts',
  'pants',
  'outerwear',
  'footwear',
  'suiting',
  'accessories',
  'grooming',
  'other',
] as const;
export const FEEDBACK_VERDICTS = ['more', 'less'] as const;

export type DealSource = (typeof DEAL_SOURCES)[number];
export type DealStatus = (typeof DEAL_STATUSES)[number];
export type DealCategory = (typeof DEAL_CATEGORIES)[number];
export type FeedbackVerdict = (typeof FEEDBACK_VERDICTS)[number];

// The feed's default cut. Everything fetched is stored (dedupe needs it), but
// below this a deal isn't worth a row of attention.
export const DEFAULT_MIN_SCORE = 40;

// ---- Ingest wire ---------------------------------------------------------

// The scorer's verdict. Null for the whole object when scoring failed — those
// items are still ingested (never lost) and re-scored on the next run, because
// "seen" means exists AND scored.
export const llmVerdictSchema = z.object({
  score: z.number(),
  category: z.enum(DEAL_CATEGORIES),
  // Only ever set from an explicit price in the post text. The scorer is told
  // never to estimate one from brand knowledge, so null means "not stated".
  est_price_usd: z.number().nonnegative().nullable(),
  est_list_price_usd: z.number().nonnegative().nullable(),
  why: z.string().max(280),
  model: z.string().max(60),
  scored_at: z.string(),
});

export const ingestItemSchema = z.object({
  source_id: z.string().min(1).max(60),
  title: z.string().min(1).max(400),
  url: z.string().url().max(2000),
  permalink: z.string().url().max(2000),
  thumbnail_url: z.string().url().max(2000).nullable(),
  posted_at: z.string(),
  llm: llmVerdictSchema.nullable(),
});

export const ingestPayloadSchema = z.object({
  source: z.enum(DEAL_SOURCES),
  fetched_at: z.string(),
  // One Reddit /new page is 100 posts; the cap leaves room without inviting an
  // unbounded write.
  items: z.array(ingestItemSchema).max(200),
});

export type LlmVerdict = z.infer<typeof llmVerdictSchema>;
export type IngestItem = z.infer<typeof ingestItemSchema>;
export type IngestPayload = z.infer<typeof ingestPayloadSchema>;

export interface IngestResult {
  inserted: number;
  updated: number;
}

// ---- Read model ----------------------------------------------------------

export interface Deal {
  id: string;
  source: DealSource;
  sourceId: string;
  title: string;
  url: string;
  permalink: string;
  thumbnailUrl: string | null;
  priceCents: number | null;
  listPriceCents: number | null;
  category: DealCategory | null;
  score: number | null;
  reason: string | null;
  status: DealStatus;
  postedAt: string;
  createdAt: string;
  updatedAt: string;
}

// ---- Money ---------------------------------------------------------------
// Stored as integer cents so no ranking or discount ever depends on float
// equality; the wire carries dollars because that is what the scorer emits.

export function usdToCents(usd: number | null | undefined): number | null {
  if (usd == null || !Number.isFinite(usd) || usd < 0) return null;
  return Math.round(usd * 100);
}

export function formatCents(cents: number | null): string | null {
  if (cents == null) return null;
  const dollars = cents / 100;
  // Whole dollars read cleaner in a dense feed; cents matter under $100.
  return cents % 100 === 0
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Null unless both prices are known and the discount is real — a 0% or
// negative "discount" is noise, and a rounded 1% is not worth a badge.
export function discountPct(
  priceCents: number | null,
  listPriceCents: number | null,
): number | null {
  if (priceCents == null || listPriceCents == null) return null;
  if (listPriceCents <= 0 || priceCents < 0) return null;
  if (priceCents >= listPriceCents) return null;
  // Test the true ratio before rounding: a 0.5% saving must not be rounded up
  // into a "1% off" badge that overstates the deal.
  const exact = (1 - priceCents / listPriceCents) * 100;
  return exact >= 1 ? Math.round(exact) : null;
}

// ---- Scoring -------------------------------------------------------------

// The schema deliberately carries no numeric bounds (the structured-output API
// rejects them, and a stray 101 would throw out the whole batch client-side),
// so the clamp lives here instead.
export function clampScore(score: number): number {
  if (!Number.isFinite(score)) return 0;
  return Math.min(100, Math.max(0, Math.round(score)));
}

export type ScoreBand = 'act' | 'solid' | 'marginal' | 'skip';

export function scoreBand(score: number | null): ScoreBand {
  if (score == null) return 'skip';
  if (score >= 80) return 'act';
  if (score >= 60) return 'solid';
  if (score >= DEFAULT_MIN_SCORE) return 'marginal';
  return 'skip';
}

// ---- Display -------------------------------------------------------------

// Deals go stale fast, so the card leads with age, not a date. Coarse on
// purpose: past a week the exact number stops mattering.
export function relativeAge(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor((now.getTime() - then) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

// Feed cards show where a deal actually leads, not the full affiliate URL.
export function urlDomain(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}
